import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Mic, Square, UserPlus } from 'lucide-react'

import { useTabNavigationStore } from '../../store/tabNavigationStore'
import { useAuthStore } from '../../store/authStore'
import { BackButton } from '../../components/ui/BackButton'
import { BottomSheet, Button, Card } from '../../components/ui'
import { useVoiceRecorder } from '../../hooks/useVoiceRecorder'
import { athletesApi } from '../../api/athletes'
import { trainingSessionsApi } from '../../api/trainingSessions'
import { transcribeApi } from '../../api/transcribeApi'
import { ru } from '../../i18n/ru'
import { translateError } from '../../i18n/translateError'
import {
  formatProgress,
  formatSessionTitle,
  nextToggleStatus,
  PRIMARY_STATUSES,
  SECONDARY_STATUSES,
  todayISO,
} from '../../utils/attendance'
import type { APIError, AttendanceStatus, RosterEntry, SessionBoard } from '../../types/api'
import { RosterList, StatusPicker } from './RosterList'

/**
 * Экран занятия: перекличка.
 *
 * Посещаемость отмечают на всю группу разом, стоя в зале, телефон в одной руке.
 * Поэтому: один тап на строку, сохранение сразу, кнопки «Сохранить» нет.
 */
export function AttendanceScreen() {
  const { getCurrentScreen, popScreen } = useTabNavigationStore()
  const currentAssistantId = useAuthStore((s) => s.currentAssistantId)
  const queryClient = useQueryClient()

  const params = getCurrentScreen().params ?? {}
  const groupId = params.groupId as string | undefined
  const date = (params.date as string | undefined) ?? todayISO()
  const startTime = params.startTime as string | undefined

  const [detailsFor, setDetailsFor] = useState<RosterEntry | null>(null)
  const [pending, setPending] = useState<Set<string>>(new Set())
  const [voiceReply, setVoiceReply] = useState<string | null>(null)
  const [voiceFailure, setVoiceFailure] = useState<string | null>(null)
  // Голос выключен до ухода с экрана: повтор дал бы тот же отказ, но каждый раз
  // заново гонял бы распознавание речи. Отметки тапами это не трогает.
  const [voiceBlocked, setVoiceBlocked] = useState(false)
  const [isAddingAthlete, setIsAddingAthlete] = useState(false)
  const [newAthlete, setNewAthlete] = useState({ name: '', age: '' })

  const { data: board, isLoading, error } = useQuery({
    queryKey: ['training-session', groupId, date, startTime],
    queryFn: () =>
      trainingSessionsApi.resolve({
        assistant_id: currentAssistantId!,
        group_id: groupId!,
        date,
        start_time: startTime,
      }),
    enabled: !!groupId && !!currentAssistantId,
  })

  const setBoard = useCallback(
    (next: SessionBoard) => {
      queryClient.setQueryData(['training-session', groupId, date, startTime], next)
    },
    [queryClient, groupId, date, startTime]
  )

  const withPending = useCallback(async (athleteId: string, action: () => Promise<SessionBoard>) => {
    setPending((prev) => new Set(prev).add(athleteId))
    try {
      return await action()
    } finally {
      setPending((prev) => {
        const next = new Set(prev)
        next.delete(athleteId)
        return next
      })
    }
  }, [])

  const markMutation = useMutation({
    mutationFn: ({ entry, status }: { entry: RosterEntry; status: AttendanceStatus | null }) =>
      withPending(entry.athlete_id, () =>
        status === null
          ? trainingSessionsApi.unmark(board!.session_id, entry.athlete_id)
          : trainingSessionsApi.mark(board!.session_id, entry.athlete_id, status)
      ),
    onSuccess: setBoard,
  })

  const allPresentMutation = useMutation({
    mutationFn: () =>
      trainingSessionsApi.rollCall({
        assistant_id: currentAssistantId!,
        group_id: groupId!,
        date,
        start_time: startTime,
        mark_all_present: true,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['training-session', groupId, date, startTime] })
    },
  })

  const addAthleteMutation = useMutation({
    mutationFn: () =>
      athletesApi.create({
        assistant_id: currentAssistantId!,
        name: newAthlete.name.trim(),
        group_id: board!.group_id,
        date_of_birth: birthDateFromAge(newAthlete.age),
      }),
    onSuccess: async (created) => {
      // Сразу отмечаем присутствие: новичок пришёл именно на эту тренировку.
      const updated = await trainingSessionsApi.mark(
        board!.session_id,
        created.id,
        'present',
        'первое занятие'
      )
      setBoard(updated)
      setIsAddingAthlete(false)
      setNewAthlete({ name: '', age: '' })
      void queryClient.invalidateQueries({ queryKey: ['athletes', currentAssistantId] })
    },
  })

  const cancelMutation = useMutation({
    mutationFn: (cancelled: boolean) =>
      trainingSessionsApi.setStatus(board!.session_id, cancelled ? 'cancelled' : 'held'),
    onSuccess: setBoard,
  })

  const handleToggle = useCallback(
    (entry: RosterEntry) => {
      markMutation.mutate({ entry, status: nextToggleStatus(entry.status) })
    },
    [markMutation]
  )

  const handlePickStatus = useCallback(
    (status: AttendanceStatus) => {
      if (!detailsFor) return
      markMutation.mutate({ entry: detailsFor, status })
      setDetailsFor(null)
    },
    [detailsFor, markMutation]
  )

  // --- Голос ---
  const voice = useVoiceRecorder()

  const handleVoice = useCallback(async () => {
    if (voiceBlocked) return
    setVoiceFailure(null)

    if (voice.state !== 'recording') {
      await voice.startRecording()
      return
    }

    const blob = await voice.stopRecording()
    if (!blob || !currentAssistantId || !board) return

    voice.setProcessing()
    try {
      // group_id сужает подсказку распознавания до состава этой группы:
      // полтора десятка фамилий вместо всей базы.
      const { text } = await transcribeApi.transcribe(currentAssistantId, blob, groupId)
      if (!text.trim()) {
        setVoiceFailure('Ничего не расслышал. Попробуй ещё раз.')
        return
      }

      const { reply, board: updated } = await trainingSessionsApi.voiceRollCall(board.session_id, {
        assistant_id: currentAssistantId,
        text,
      })

      setBoard(updated)
      // Показываем разбор явно: в зале шумно, и тихо применённая неверная
      // отметка хуже, чем её отсутствие.
      setVoiceReply(reply)
    } catch (err) {
      const failure = describeVoiceError(err)
      setVoiceFailure(failure.text)
      if (failure.blocksVoice) setVoiceBlocked(true)
    } finally {
      voice.reset()
    }
  }, [voice, voiceBlocked, currentAssistantId, board, groupId, setBoard])

  useEffect(() => {
    if (voice.error) setVoiceFailure(voice.error)
  }, [voice.error])

  const sortedRoster = useMemo(() => board?.roster ?? [], [board])

  if (!groupId) {
    return <CenteredNote text="Не выбрана группа" />
  }
  if (isLoading) {
    return <CenteredNote text="Открываем занятие…" />
  }
  if (error || !board) {
    return <CenteredNote text="Не удалось открыть занятие" />
  }

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: 'var(--bg-primary)' }}>
      <div className="flex items-center gap-2 p-4">
        <BackButton onClick={popScreen} />
        <div className="flex-1">
          <h1 className="text-[17px] font-semibold" style={{ color: 'var(--text-primary)' }}>
            {formatSessionTitle(board)}
          </h1>
        </div>
        <span
          className="text-[15px] font-semibold"
          style={{ color: 'var(--text-secondary)' }}
          data-testid="attendance-progress"
        >
          {formatProgress(board)}
        </span>
      </div>

      {board.cancelled ? (
        <div className="px-4 pb-4">
          <Card>
            <p className="py-2 text-center text-[15px]" style={{ color: 'var(--text-secondary)' }}>
              Тренировка отмечена как отменённая
            </p>
            <Button variant="secondary" onClick={() => cancelMutation.mutate(false)}>
              Всё-таки была
            </Button>
          </Card>
        </div>
      ) : (
        <div className="flex gap-2 px-4 pb-3">
          <Button
            variant="secondary"
            onClick={() => allPresentMutation.mutate()}
            disabled={allPresentMutation.isPending}
          >
            <Check size={18} /> Все здесь
          </Button>
          <Button
            variant={voice.state === 'recording' ? 'primary' : 'secondary'}
            onClick={() => void handleVoice()}
            disabled={voice.state === 'processing' || voiceBlocked}
            aria-label={voice.state === 'recording' ? 'Остановить запись' : 'Записать голосом'}
          >
            {voice.state === 'recording' ? <Square size={18} /> : <Mic size={18} />}
            {voice.state === 'processing' ? 'Разбираю…' : 'Голосом'}
          </Button>
        </div>
      )}

      {voiceReply && (
        <div className="px-4 pb-3">
          <Card>
            <p className="whitespace-pre-line text-[15px]" style={{ color: 'var(--text-primary)' }}>
              {voiceReply}
            </p>
            <Button variant="secondary" onClick={() => setVoiceReply(null)}>
              Понятно
            </Button>
          </Card>
        </div>
      )}

      {voiceFailure && (
        <p className="px-4 pb-3 text-[15px]" style={{ color: 'var(--danger)' }}>
          {voiceFailure}
        </p>
      )}

      <RosterList
        roster={sortedRoster}
        onToggle={handleToggle}
        onOpenDetails={setDetailsFor}
        pendingAthleteIds={pending}
        disabled={board.cancelled}
      />

      {!board.cancelled && (
        <div className="px-4 pt-4">
          <Button variant="secondary" onClick={() => setIsAddingAthlete(true)}>
            <UserPlus size={18} /> Новенький
          </Button>
        </div>
      )}

      <BottomSheet
        isOpen={isAddingAthlete}
        onClose={() => setIsAddingAthlete(false)}
        title="Новенький"
        height="50%"
      >
        <div className="flex flex-col gap-3 p-4">
          <input
            type="text"
            value={newAthlete.name}
            onChange={(e) => setNewAthlete((prev) => ({ ...prev, name: e.target.value }))}
            placeholder="Имя и фамилия"
            aria-label="Имя и фамилия"
            style={sheetInputStyle}
          />
          <input
            type="number"
            inputMode="numeric"
            value={newAthlete.age}
            onChange={(e) => setNewAthlete((prev) => ({ ...prev, age: e.target.value }))}
            placeholder="Сколько лет"
            aria-label="Сколько лет"
            style={sheetInputStyle}
          />
          <Button
            variant="primary"
            onClick={() => addAthleteMutation.mutate()}
            disabled={!newAthlete.name.trim() || addAthleteMutation.isPending}
          >
            Добавить и отметить
          </Button>
        </div>
      </BottomSheet>

      <BottomSheet
        isOpen={!!detailsFor}
        onClose={() => setDetailsFor(null)}
        title={detailsFor?.name}
        height="50%"
      >
        <StatusPicker
          current={detailsFor?.status}
          statuses={[...PRIMARY_STATUSES, ...SECONDARY_STATUSES]}
          onPick={handlePickStatus}
        />
      </BottomSheet>
    </div>
  )
}

/** Что сказать тренеру после неудачного голосового разбора. */
export interface VoiceFailure {
  text: string
  /** Повтор гарантированно даст тот же отказ - кнопку голоса выключаем */
  blocksVoice: boolean
}

const VOICE_OFF_HINT = 'Голос недоступен, отметки тапами работают.'

/**
 * Разбирает ошибку голосовой переклички (APIError из интерцептора).
 *
 * Голос идёт через чат: расходует месячную квоту AI-запросов и попадает под
 * soft lock. На такие отказы «попробуй ещё раз» вредно - тренер перезапишет
 * фразу, снова оплатит распознавание и снова получит отказ.
 */
export function describeVoiceError(err: unknown): VoiceFailure {
  const apiError = (err && typeof err === 'object' ? err : {}) as Partial<APIError>

  if (apiError.status === 401) {
    return {
      text: 'Сессия истекла, войди заново. Отметки, что уже стоят, сохранены.',
      blocksVoice: true,
    }
  }

  // 429 бэкенд отдаёт только на исчерпанную квоту (code: ai_limit_exceeded)
  if (apiError.status === 429 || apiError.code === 'ai_limit_exceeded') {
    const usage = apiError.ai_usage
    const counts = usage ? ` (${usage.used} из ${usage.limit})` : ''
    return {
      text: `Лимит AI-запросов на этот месяц исчерпан${counts}. ${VOICE_OFF_HINT}`,
      blocksVoice: true,
    }
  }

  // Soft lock после grace period - тот же текст, что и на остальных пишущих ручках
  if (apiError.status === 423) {
    const lock =
      apiError.code === 'ERR_SOFT_LOCKED' ? translateError(apiError) : ru.errors.ERR_SOFT_LOCKED
    return { text: `${lock} ${VOICE_OFF_HINT}`, blocksVoice: true }
  }

  if (apiError.user_message) {
    return { text: apiError.user_message, blocksVoice: false }
  }

  return { text: 'Не получилось разобрать запись. Попробуй ещё раз.', blocksVoice: false }
}

function CenteredNote({ text }: { text: string }) {
  return (
    <div className="p-4">
      <Card>
        <p className="py-8 text-center" style={{ color: 'var(--text-hint)' }}>
          {text}
        </p>
      </Card>
    </div>
  )
}

const sheetInputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px',
  borderRadius: 10,
  border: '1px solid var(--separator)',
  backgroundColor: 'var(--bg-secondary)',
  color: 'var(--text-primary)',
  fontSize: 17,
}

/**
 * Дата рождения из возраста, названного на тренировке.
 *
 * Точной даты у тренера под рукой нет, поэтому пишем 1 января подходящего года:
 * приблизительно, зато спортсмен сразу попадает в нужную возрастную группу,
 * а дату потом можно уточнить в карточке.
 */
export function birthDateFromAge(age: string, now: Date = new Date()): string | undefined {
  const years = Number(age)
  if (!Number.isInteger(years) || years <= 0 || years > 120) return undefined
  return `${now.getFullYear() - years}-01-01`
}
